import { useGenerateLossByPercentageCDFs } from 'features/dataLayers'
import { useRolodexEntries } from 'features/rolodex'
import { useMemo } from 'react'
import {
  ForecastComponentConfig,
  GeckoAssetModel,
  GeckoComponent,
  GeckoComponentDataFrame,
  GeckoComponentDataset,
  GeckoEntryBase,
  GeckoInspection,
  GeckoPublishingState,
  isInspectionThicknessCDF,
  RolodexEntryType,
} from 'types'

function getDatasetKey(componentId?: string, inspectionId?: string) {
  if (!componentId || !inspectionId) return undefined
  return `${componentId}::${inspectionId}`
}

export function getPreferredReportComponentDatasets(
  componentDatasets: ReadonlyArray<GeckoComponentDataset>
): GeckoComponentDataset[] {
  const datasetsByComponentInspection = new Map<
    string,
    GeckoComponentDataset[]
  >()

  componentDatasets.forEach((dataset) => {
    const key = getDatasetKey(dataset.tags.component, dataset.tags.inspection)
    if (!key) return

    const currentDatasets = datasetsByComponentInspection.get(key) ?? []
    currentDatasets.push(dataset)
    datasetsByComponentInspection.set(key, currentDatasets)
  })

  return Array.from(datasetsByComponentInspection.values())
    .map((datasets) => {
      const finalizedDatasets = datasets
        .filter(
          (dataset) =>
            dataset.data.publishing_state === GeckoPublishingState.Finalized
        )
        .sort(GeckoComponentDataset.DefaultSort)

      return finalizedDatasets.at(0)
    })
    .filter((dataset): dataset is GeckoComponentDataset => !!dataset)
}

export function filterCdfsToPreferredReportDatasets(
  componentDataFrames: ReadonlyArray<GeckoComponentDataFrame>,
  preferredComponentDatasets: ReadonlyArray<GeckoComponentDataset>
): GeckoComponentDataFrame[] {
  const preferredDatasetIds = new Set(
    preferredComponentDatasets.map((dataset) => dataset.id)
  )

  return componentDataFrames.filter((cdf) => {
    const datasetId = cdf.tags['component-dataset']
    return !datasetId || preferredDatasetIds.has(datasetId)
  })
}

export function mergeSelectableComponentCdfs(params: {
  componentId: string | undefined
  relevantCDFs: ReadonlyArray<GeckoComponentDataFrame> | undefined
  generatedLossByPercentageCDFs: ReadonlyArray<GeckoComponentDataFrame>
  componentDatasets: ReadonlyArray<GeckoComponentDataset>
}): GeckoComponentDataFrame[] {
  const {
    componentId,
    relevantCDFs,
    generatedLossByPercentageCDFs,
    componentDatasets,
  } = params

  if (!componentId) return []

  const mergedCdfs = [
    ...(relevantCDFs?.filter(
      (cdf) => cdf.data.geometry.componentID === componentId
    ) ?? []),
    ...generatedLossByPercentageCDFs,
  ]

  const cdfsById = new Map<string, GeckoComponentDataFrame>()
  mergedCdfs.forEach((cdf) => {
    cdfsById.set(cdf.id, cdf)
  })

  return Array.from(cdfsById.values()).sort(
    GeckoComponentDataFrame.GetDefaultSort(componentDatasets)
  )
}

export function useSelectableComponentCDFs(params: {
  selectedComponent: GeckoComponent | undefined
  relevantCDFs: ReadonlyArray<GeckoComponentDataFrame> | undefined
  componentDatasets: ReadonlyArray<GeckoComponentDataset>
  selectedAssetModel: GeckoAssetModel | undefined
  inspections: readonly GeckoInspection[]
}) {
  const {
    selectedComponent,
    relevantCDFs,
    componentDatasets,
    selectedAssetModel,
    inspections,
  } = params

  const { entries: forecastComponentConfigEntries } = useRolodexEntries(
    selectedComponent
      ? {
          type: RolodexEntryType.FDE_FORECAST_COMPONENT_CONFIG,
          tags: {
            [RolodexEntryType.COMPONENT]: selectedComponent.id,
          },
        }
      : undefined
  )

  const forecastComponentConfig = useMemo(() => {
    return forecastComponentConfigEntries.find(
      (entry): entry is GeckoEntryBase<ForecastComponentConfig> =>
        entry.type === RolodexEntryType.FDE_FORECAST_COMPONENT_CONFIG &&
        entry.tags.component === selectedComponent?.id
    )
  }, [forecastComponentConfigEntries, selectedComponent?.id])

  const thicknessCDFs = useMemo(() => {
    if (!selectedComponent || !relevantCDFs) return []

    return relevantCDFs.filter(
      (cdf) =>
        cdf.data.geometry.componentID === selectedComponent.id &&
        isInspectionThicknessCDF(cdf)
    )
  }, [relevantCDFs, selectedComponent])

  const generatedLossByPercentageCDFs = useGenerateLossByPercentageCDFs(
    selectedAssetModel,
    thicknessCDFs,
    inspections,
    selectedComponent?.id,
    forecastComponentConfig,
    componentDatasets
  )

  return useMemo(() => {
    return mergeSelectableComponentCdfs({
      componentId: selectedComponent?.id,
      relevantCDFs,
      generatedLossByPercentageCDFs,
      componentDatasets,
    })
  }, [
    componentDatasets,
    generatedLossByPercentageCDFs,
    relevantCDFs,
    selectedComponent?.id,
  ])
}
